import styled, { css } from 'styled-components'
import { HELPER_CLASS } from './consts'
import type { SortableGalleryProps } from './types'
import { mediaQueries } from '../../providers/MediaQueryProvider'

const WIDTH = 85
const HEIGHT = 114

export const Image = styled.img<{ inactive?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: var(--radius-brand);

  ${({ inactive }) =>
    inactive &&
    `
    opacity: 0.4;
  `}
`

export const DragAndDropAnchorWrapper = styled.div`
  display: none;

  ${mediaQueries.tablet} {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    z-index: 1;
  }
`

export const DragAndDropAnchorButton = styled.div`
  background: var(--bg-primary);
  padding: var(--space-150);
  margin-top: var(--space-100);
  margin-left: var(--space-100);
  border: 1px solid var(--border-secondary);
  border-radius: var(--radius-brand);
  cursor: grab;

  svg {
    path {
      stroke: var(--icon-primary);
      stroke-width: 1;
    }
  }

  &:hover {
    background-color: var(--bg-secondary);
  }
`

export const TrashButtonWrapper = styled.div`
  display: none;
  cursor: pointer;

  ${mediaQueries.tablet} {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    opacity: 0;
    z-index: 1;
  }
`

export const TrashButton = styled.div`
  background: var(--bg-primary);
  padding: var(--space-150);
  margin-top: var(--space-100);
  margin-right: var(--space-100);
  border: 1px solid var(--border-secondary);
  border-radius: var(--radius-brand);

  &:hover {
    background-color: var(--bg-secondary);
  }

  svg {
    path {
      stroke: var(--icon-primary);
      stroke-width: 1;
    }
  }
`

export const ImageContainer = styled.div<Pick<SortableGalleryProps, 'disabled'>>`
  position: relative;
  aspect-ratio: 3/4;
  background-color: var(--bg-secondary);

  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border: 2px solid transparent;
  }

  &:hover {
    &:after {
      border: 1px solid var(--color-black-alpha-800);
    }

    ${TrashButtonWrapper} {
      opacity: 1;
    }

    ${DragAndDropAnchorWrapper} {
      opacity: 1;
    }
  }
`

export const LoaderContainer = styled.div`
  aspect-ratio: 3/4;
  background-color: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: var(--font-line-height-400);
  justify-content: center;
`

export const Item = styled.div<Pick<SortableGalleryProps, 'disabled'>>`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'grab')};

  grid-column: span 1;
  text-align: center;

  &.${HELPER_CLASS} {
    pointer-events: auto !important;
    cursor: grabbing;
  }

  &:hover {
    z-index: 2;
  }
`

export const ItemWrapper = styled.div`
  position: relative;
`

export const Placeholder = styled.div`
  position: absolute;
  background-color: var(--bg-secondary);
`

export const UploadZone = styled.div``

export const Container = styled.div<{ columns?: number }>`
  display: grid;
  user-select: none;

  ${({ columns }) =>
    columns
      ? `grid-template-columns: repeat(${columns}, 1fr);`
      : `grid-template-columns: repeat(auto-fill, minmax(${WIDTH}px, 1fr));`}

  ${({ columns }) =>
    columns
      ? css`
          ${ImageContainer} {
            min-width: ${WIDTH}px;
            min-height: ${HEIGHT}px;
          }

          ${LoaderContainer} {
            min-width: ${WIDTH}px;
            min-height: ${HEIGHT}px;
          }
        `
      : ''};

  gap: var(--space-200);
  width: 100%;
  margin: 0 auto;

  ${UploadZone} {
    min-width: ${WIDTH}px;
    min-height: ${HEIGHT}px;
    aspect-ratio: 3/4;
  }
`

export const Divider = styled.div`
  grid-column: 1 / -1;
  margin: var(--space-200) 0;

  svg {
    path {
      stroke: var(--icon-secondary);
      stroke-width: 1;
    }
  }
`

export const DividerText = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-150);
  z-index: 2;
`

export const UploadZoneWrapper = styled.div`
  aspect-ratio: 3/4;
  border-radius: var(--radius-brand);
`
